// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { Sans14, Sans16, spacing, typography } from "@recidiviz/design-system";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { rem, rgba } from "polished";
import React, { useState } from "react";
import styled from "styled-components";
import superjson from "superjson";

import { palette } from "~design-system";

import {
  useFeatureVariants,
  useRootStore,
} from "../../components/StoreProvider";
import useIsMobile from "../../hooks/useIsMobile";
import { Client, OpportunityNotification } from "../../WorkflowsStore";
import { Resident } from "../../WorkflowsStore/Resident";
import { CaseNoteSearch } from "../CaseNoteSearch";
import { trpc } from "../CaseNoteSearch/trpc";
import { usePersonTracking } from "../hooks/usePersonTracking";
import OpportunityNotifications from "../OpportunityCaseloadView/OpportunityNotifications";
import { ProfileCapsule } from "../PersonCapsules";
import {
  SectionCard,
  SectionCardBody,
  SectionCardHeader,
} from "../SectionCard";
import { CaseloadTasksHydrator } from "../TasksHydrator/TasksHydrator";
import { WorkflowsNavLayout } from "../WorkflowsLayouts";
import AddedTasks from "../WorkflowsTasks/AddedTasks";
import { PreviewTasks } from "../WorkflowsTasks/PreviewTasks";
import {
  FilterHeaderRow,
  TaskSectionFilter,
} from "../WorkflowsTasks/TaskSectionFilter";
import {
  ClientEmployer,
  ClientHousing,
  FinesAndFees,
  Milestones,
  SpecialConditions,
} from "./ClientDetailSidebarComponents";
import { UsUtDates } from "./ClientDetailSidebarComponents/UsUtDates";
import ClientDetailsInput from "./ClientDetailsInput";
import {
  NoOpportunities,
  OpportunitiesAccordion,
} from "./OpportunitiesAccordion";
import { UsIaActionPlansAndNotes } from "./OpportunityDetailSidebarComponents";
import {
  ClientDetailComponentName,
  ClientDetailSidebarComponents,
  DetailsSection as SidebarComponentSection,
} from "./OpportunityProfile";
import { PreferredContact } from "./PreferredContact";
import { ResidentHousing } from "./ResidentDetailSidebarComponents";
import { SentenceProgress } from "./SentenceProgress";
import { SentenceProgressV2 } from "./SentenceProgressV2";
import { Divider, PhoneNumber } from "./styles";
import {
  ClientProfileProps,
  PersonProfileProps,
  ResidentProfileProps,
} from "./types";
import { UsArResidentInformation } from "./UsAr/UsArResidentInformation";
import { UsAzResidentInformation } from "./UsAz/UsAzResidentInformation";
import { UsIdResidentInformation } from "./UsId/UsIdResidentInformation";
import { UsMoCaseOverview } from "./UsMo/CaseOverview/UsMoCaseOverview";
import { UsMoCasePlanning } from "./UsMo/CasePlanning";
import { RecentCaseNotes } from "./UsMo/RecentCaseNotes";
import { UsMoResidentInformation } from "./UsMo/UsMoResidentInformation";
import { UsNdResidentInformation } from "./UsNd/UsNdResidentInformation";
import { UsUtResidentInformation } from "./UsUt/UsUtResidentInformation";

const COLUMNS = "1fr 1.2fr";

const GUTTER = rem(spacing.sm * 15);
const SMALL_GUTTER = rem(spacing.md);

const Wrapper = styled.div<{
  isMobile: boolean;
}>`
  display: grid;
  column-gap: ${GUTTER};
  padding-bottom: ${rem(spacing.lg)};
  row-gap: ${SMALL_GUTTER};

  ${({ isMobile }) =>
    !isMobile &&
    `div[class*="AccordionWrapper"] {
      margin: 0;
    }`}
`;

const Header = styled.div<{ isMobile: boolean }>`
  border-bottom: 1px solid ${rgba(palette.slate, 0.15)};
  gap: ${({ isMobile }) => (isMobile ? rem(spacing.xl) : SMALL_GUTTER)};
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? `100%` : COLUMNS)};
  padding-top: 0;
  padding-bottom: ${rem(spacing.md)};
  cursor: default;
  min-width: ${rem(280)};
`;

const Content = styled.div<{
  isMobile: boolean;
}>`
  display: grid;
  gap: ${SMALL_GUTTER};
  grid-template-columns: ${({ isMobile }) => (isMobile ? `100%` : COLUMNS)};

  & > div {
    :last-child {
      ${({ isMobile }) => isMobile && `order: -1`}
    }
  }

  hr[class*="TaskItemDivider"] {
    margin: 0;
  }
`;

const ContactDetailsContainer = styled.div<{
  isMobile: boolean;
}>`
  display: grid;
  gap: ${rem(spacing.md)};
  grid-template-columns: ${({ isMobile }) =>
    isMobile ? `repeat(auto-fill, minmax(${rem(176)}, 1fr));` : `1fr 1fr 1fr;`};

  ${({ isMobile }) =>
    `max-width: ${isMobile ? "unset" : rem(500)}; justify-self: ${
      isMobile ? "unset" : "end"
    };`}
`;

const ContactCell = styled.dl`
  ${typography.Sans14}
  color: ${palette.slate70};
  margin: 0;
  padding: 0;
`;

const ContactLabel = styled.dt`
  font-weight: inherit;
`;

const ContactValue = styled.dd`
  ${typography.Sans16};
  color: ${palette.pine2};
  margin: 0;
  padding-top: 5px;
  text-align: left;

  :first-child {
    padding-top: 0;
  }
`;

const SectionHeading = styled(Sans16)`
  color: ${palette.slate80};
  margin-bottom: ${rem(spacing.md)};
`;

const TimelineHeading = styled(Sans16)`
  color: ${palette.slate80};
`;

// Full-bleed, borderless, shorter empty state for the US_MO Tasks card so it
// reads as part of the card rather than a nested card. Standalone (intentionally
// not derived from NoOpportunities, which the Opportunities empty state owns).
const TasksCardEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${rgba(palette.slate, 0.05)};
  color: ${palette.slate70};
  height: 7.625rem;
  margin: 0 -${rem(spacing.md)};
  padding: ${rem(spacing.md)} 0;

  ${Sans16} {
    color: ${palette.pine2};
  }
`;

// The US_MO Tasks card stacks two sections; the Added Tasks header gets a top
// border so it stays separated from the (borderless, full-bleed) Tasks section
// above it.
const AddedTasksCardHeader = styled(SectionCardHeader)`
  border-top: 1px solid ${palette.slate10};
`;

const CasenoteSearchWrapper = styled.div`
  & ${Divider} {
    display: block;
    margin-bottom: 0;
  }
`;

const ProfileDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.md)};
`;

// Right column of the profile grid. Stacks the recent case notes (or
// opportunities) above the US_MO Case Planning module.
const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(32)};
`;

function AdditionalDetails({
  person,
}: PersonProfileProps): React.ReactElement<any> {
  if (person instanceof Client) {
    return <ClientDetails client={person} />;
  }

  if (person instanceof Resident) {
    return <ResidentDetails resident={person} />;
  }

  return <div />;
}

/**
 * Maps a tenant's `clientProfileConfig` section list over the shared
 * `ClientDetailSidebarComponents` registry
 */
function renderConfiguredClientProfileSections(
  sections: ClientDetailComponentName[] | undefined,
  client: Client,
): React.ReactElement<any> | null {
  if (!sections?.length) return null;

  return (
    <>
      {sections.map((componentName) => {
        const Component = ClientDetailSidebarComponents[componentName];
        return (
          <SidebarComponentSection key={componentName}>
            <Component client={client} />
          </SidebarComponentSection>
        );
      })}
    </>
  );
}

function AdditionalDetailsWithOptionalHeading({
  person,
  sidebarHeadingText,
  showHeading,
}: PersonProfileProps & {
  sidebarHeadingText: string;
  showHeading: boolean;
}): React.ReactElement<any> {
  if (!showHeading) {
    return <AdditionalDetails person={person} />;
  }

  return (
    <div>
      <SectionHeading>{sidebarHeadingText}</SectionHeading>
      <Divider />
      <AdditionalDetails person={person} />
    </div>
  );
}

const ClientDetails = observer(function ClientDetails({
  client,
}: ClientProfileProps): React.ReactElement<any> {
  const { clientProfileWarmHandoff } = useFeatureVariants();
  const { tenantStore } = useRootStore();

  const configuredSections = clientProfileWarmHandoff
    ? renderConfiguredClientProfileSections(
        tenantStore.clientProfileSections,
        client,
      )
    : null;
  if (configuredSections) return configuredSections;

  return (
    <>
      {client.stateCode === "US_UT" && <UsUtDates client={client} />}
      {client.profileMilestones.length > 0 && (
        <>
          <Milestones client={client} />
          <Divider />
        </>
      )}
      {client.address && (
        <>
          <ClientHousing client={client} /> <Divider />
        </>
      )}
      {client.currentEmployers && client.currentEmployers.length > 0 && (
        <>
          <ClientEmployer client={client} />
          <Divider />
        </>
      )}
      {client.currentBalance !== undefined && (
        <>
          <FinesAndFees client={client} />
          <Divider />
        </>
      )}
      <SpecialConditions client={client} />
      {client.opportunities.usIaEarlyDischarge?.length && (
        <UsIaActionPlansAndNotes
          opportunity={client.opportunities.usIaEarlyDischarge[0]}
        />
      )}
    </>
  );
});

const ResidentDetails = observer(function ResidentDetails({
  resident,
}: ResidentProfileProps): React.ReactElement<any> {
  return (
    <>
      <ResidentHousing resident={resident} />
      <StateSpecificResidentInformation resident={resident} />
      <Divider />
    </>
  );
});

function StateSpecificResidentInformation({
  resident,
}: ResidentProfileProps): React.ReactElement<any> | null {
  switch (resident.stateCode) {
    case "US_AR":
      return <UsArResidentInformation resident={resident} />;
    case "US_AZ":
      return <UsAzResidentInformation resident={resident} />;
    case "US_ID":
      return <UsIdResidentInformation resident={resident} />;
    case "US_MO":
      return <UsMoResidentInformation resident={resident} />;
    case "US_ND":
      return <UsNdResidentInformation resident={resident} />;
    case "US_UT":
      return <UsUtResidentInformation resident={resident} />;
    default:
      return null;
  }
}

const PreferredName: React.FC<ClientProfileProps> = observer(
  function PreferredName({ client }): React.ReactElement<any> {
    const name =
      client.preferredName || toJS(client.fullName).givenNames || "Unknown";
    return (
      <ClientDetailsInput
        text={name}
        client={client}
        updateType="preferredName"
      />
    );
  },
);

type ContactDetailsType = { isMobile: boolean } & PersonProfileProps;

function ContactDetails({
  person,
  isMobile,
}: ContactDetailsType): React.ReactElement<any> | null {
  if (!(person instanceof Client)) return null;

  return (
    <ContactDetailsContainer isMobile={isMobile}>
      <ContactCell>
        <ContactLabel>Contacts</ContactLabel>
        <ContactValue className="fs-exclude">
          {person.phoneNumber ? (
            <PhoneNumber href={person.phoneNumberUri}>
              {person.phoneNumber}
            </PhoneNumber>
          ) : (
            "Phone number unavailable"
          )}
        </ContactValue>
        <ContactValue className="fs-exclude">
          {person.emailAddress || "Email unavailable"}
        </ContactValue>
      </ContactCell>
      <ContactCell>
        <ContactLabel>Preferred Name</ContactLabel>
        <ContactValue>
          <PreferredName client={person} />
        </ContactValue>
      </ContactCell>
      <ContactCell>
        <ContactLabel>Preferred Contact</ContactLabel>
        <ContactValue>
          <PreferredContact client={person} />
        </ContactValue>
      </ContactCell>
    </ContactDetailsContainer>
  );
}

export const FullProfile = observer(
  function FullProfile(): React.ReactElement<any> | null {
    const {
      workflowsStore: { selectedPerson: person },
      userStore,
      tenantStore,
    } = useRootStore();
    const { isTablet, isMobile } = useIsMobile(true);
    const {
      caseNoteSearch,
      caseOverview,
      clientProfileWarmHandoff,
      customTasks,
      hideWorkflowsOpportunities,
      recentCaseNotes,
      sentenceProgressV2,
      usMoCasePlanning,
    } = useFeatureVariants();

    // View-local filter state for the US_MO `customTasks` Tasks section.
    const [showHidden, setShowHidden] = useState(false);

    usePersonTracking(person, () => {
      person?.trackProfileViewed();
    });

    if (!person) return null;

    // MO residents don't have start/end dates or officer IDs in their resident record,
    // so we do not show the sidebar timeline for MO residents
    const isMoResident =
      person instanceof Resident && person.stateCode === "US_MO";
    const showFullWidthTimeline = !isMoResident;
    const sidebarHeadingText = showFullWidthTimeline
      ? "Additional information"
      : "Progress toward success";

    // Suppress unimplemented sections for MO clients
    const isMoClient = person instanceof Client && person.stateCode === "US_MO";

    const hideOpportunitiesSection = isMoClient || hideWorkflowsOpportunities;

    const usesConfigDrivenClientProfile =
      person instanceof Client &&
      clientProfileWarmHandoff &&
      !!tenantStore.clientProfileSections?.length;

    const queryClient = new QueryClient();

    const trpcClient = trpc.createClient({
      links: [
        httpBatchLink({
          url: import.meta.env["VITE_CASE_NOTES_API_URL"],
          async headers() {
            const token = userStore.getToken ? await userStore.getToken() : "";
            const stateCode = person.stateCode;

            return {
              Authorization: `Bearer ${token}`,
              StateCode: `${stateCode}`,
            };
          },
          // Required to get Date objects to serialize correctly.
          transformer: superjson,
        }),
      ],
    });

    const empty = (
      <NoOpportunities>
        <Sans16>None for now</Sans16>
        <Sans14>New tasks will appear here.</Sans14>
      </NoOpportunities>
    );

    // US_MO `customTasks` card uses a full-bleed, borderless, half-height empty
    // state so the Tasks section doesn't look like a card nested in a card.
    const tasksCardEmpty = (
      <TasksCardEmptyState>
        <Sans16>None for now</Sans16>
        <Sans14>New tasks will appear here.</Sans14>
      </TasksCardEmptyState>
    );

    const notifications: OpportunityNotification[] = Object.values(
      person.opportunities,
    )
      .flat()
      .flatMap((o) => o.notificationsByPage?.profile ?? []);

    return (
      <WorkflowsNavLayout>
        <Wrapper isMobile={isMobile}>
          <Header isMobile={isTablet}>
            <ProfileCapsule
              avatarSize="lg"
              person={person}
              textSize="sm"
              nameHoverState={false}
            />
            <ContactDetails person={person} isMobile={isTablet} />
          </Header>
          {caseNoteSearch && (
            <CasenoteSearchWrapper>
              <trpc.Provider client={trpcClient} queryClient={queryClient}>
                <QueryClientProvider client={queryClient}>
                  <CaseNoteSearch />
                </QueryClientProvider>
              </trpc.Provider>
              <Divider />
            </CasenoteSearchWrapper>
          )}
          {notifications.length > 0 && (
            <OpportunityNotifications notifications={notifications} />
          )}
          {showFullWidthTimeline && (
            <>
              {sentenceProgressV2 ? (
                <SentenceProgressV2 person={person} />
              ) : (
                <>
                  <TimelineHeading>Progress toward success</TimelineHeading>
                  <SentenceProgress person={person} />
                </>
              )}
              <Divider />
            </>
          )}
          <Content isMobile={isTablet}>
            <ProfileDetailsWrapper>
              {caseOverview &&
                person instanceof Client &&
                person.stateCode === "US_MO" && (
                  // `person instanceof Client && stateCode === "US_MO"`
                  // narrows `person` to a US_MO `Client`. `caseOverview` is
                  // already gated to the US_MO tenant via `activeTenants`,
                  // but a US_MO user can also view a Resident profile, so
                  // the `instanceof Client` guard keeps this path
                  // Client-only.
                  <UsMoCaseOverview client={person} />
                )}
              {person.supervisionTasks?.orderedTasks &&
                (customTasks ? (
                  <SectionCard>
                    <SectionCardHeader>
                      <FilterHeaderRow>
                        <span>Tasks</span>
                        <TaskSectionFilter
                          label="Show Hidden"
                          checked={showHidden}
                          onChange={setShowHidden}
                          testId="tasks-filter"
                        />
                      </FilterHeaderRow>
                    </SectionCardHeader>
                    <SectionCardBody>
                      <CaseloadTasksHydrator
                        empty={tasksCardEmpty}
                        hydrated={
                          <PreviewTasks
                            person={person}
                            showSnoozeDropdown={false}
                            hideSnoozed={!showHidden}
                            empty={tasksCardEmpty}
                          />
                        }
                      />
                    </SectionCardBody>
                    {customTasks && person instanceof Client && (
                      <AddedTasks
                        client={person}
                        renderShell={(body, filter) => (
                          <>
                            <AddedTasksCardHeader>
                              <FilterHeaderRow>
                                <span>Added Tasks</span>
                                {filter}
                              </FilterHeaderRow>
                            </AddedTasksCardHeader>
                            <SectionCardBody>{body}</SectionCardBody>
                          </>
                        )}
                      />
                    )}
                  </SectionCard>
                ) : (
                  <div>
                    <SectionHeading>Tasks</SectionHeading>
                    <Divider />
                    <CaseloadTasksHydrator
                      empty={empty}
                      hydrated={
                        <PreviewTasks
                          person={person}
                          showSnoozeDropdown={false}
                          empty={empty}
                        />
                      }
                    />
                  </div>
                ))}
              {isMoClient ? null : (
                <AdditionalDetailsWithOptionalHeading
                  person={person}
                  sidebarHeadingText={sidebarHeadingText}
                  showHeading={!usesConfigDrivenClientProfile}
                />
              )}
            </ProfileDetailsWrapper>
            <RightColumn>
              {recentCaseNotes && person instanceof Client ? (
                <RecentCaseNotes client={person} />
              ) : (
                !hideOpportunitiesSection && (
                  <div>
                    <SectionHeading>Opportunities</SectionHeading>
                    <OpportunitiesAccordion
                      person={person}
                      formLinkButton
                      showIneligibleOpportunityTypes
                    />
                  </div>
                )
              )}
              {usMoCasePlanning &&
                person instanceof Client &&
                person.stateCode === "US_MO" && (
                  <UsMoCasePlanning client={person} />
                )}
              {person instanceof Client &&
                clientProfileWarmHandoff &&
                renderConfiguredClientProfileSections(
                  tenantStore.clientProfileRightColumnSections,
                  person,
                )}
            </RightColumn>
          </Content>
        </Wrapper>
      </WorkflowsNavLayout>
    );
  },
);
